import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StorageService, storageStore, StorageType } from '@app/services/storage';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Endpoint, EndpointService } from '@services/endpoints/';
import { HttpShareHeaders } from '@tcp13equals2/z5-lib';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { config } from '@env/config';
import { ProgressIndicatorService } from '../progress';
import { SystemAlertsService } from '../system-alerts';
import { IAuthToken, TokenServiceKey, CustomURLEncoder } from './_models';
import { environment } from '@env/environment';
import { routePathName, routeUrl } from '@app/app.models';

@Injectable({
	providedIn: 'root'
})
export class TokenService {
	private set clientId(clientId: string) {
		this.storageService.setItem(StorageType.cookie, TokenServiceKey.clientId, clientId, storageStore);
	}

	private get clientId(): string {
		return this.storageService.getItem(StorageType.cookie, TokenServiceKey.clientId, storageStore);
	}

	private set accessToken(token: string) {
		this.storageService.setItem(StorageType.cookie, TokenServiceKey.accessToken, token, storageStore);
	}

	private get accessToken(): string {
		return this.storageService.getItem(StorageType.cookie, TokenServiceKey.accessToken, storageStore);
	}

	private set refreshToken(token: string) {
		this.storageService.setItem(StorageType.cookie, TokenServiceKey.refreshToken, token, storageStore);
	}

	private get refreshToken(): string {
		return this.storageService.getItem(StorageType.cookie, TokenServiceKey.refreshToken, storageStore);
	}

	private set tokenType(token: string) {
		this.storageService.setItem(StorageType.cookie, TokenServiceKey.tokenType, token, storageStore);
	}

	private set tokenExpiry(expires: number) {
		this.storageService.setItem(StorageType.cookie, TokenServiceKey.tokenExpiry, String(expires), storageStore);
	}

	private get tokenExpiry(): number {
		return Number(this.storageService.getItem(StorageType.cookie, TokenServiceKey.tokenExpiry, storageStore));
	}

	public get tokenExpired(): boolean {
		if (!this.tokenExpiry || !this.accessToken || !this.refreshToken) return true;

		// Get current time in seconds, minus 10 to ensure we don't have tokens expiring during the HTTP request
		const secondsSinceEpoch = Date.now() / 1000 - 10;

		return this.tokenExpiry <= secondsSinceEpoch;
	}

	public get tokenEmail(): string {
		return this.storageService.getItem(StorageType.cookie, TokenServiceKey.email, storageStore);
	}

	public set tokenEmail(email: string) {
		this.storageService.setItem(StorageType.cookie, TokenServiceKey.email, email, storageStore);
	}

	constructor(
		private http: HttpClient,
		private endpointService: EndpointService,
		private storageService: StorageService,
		private systemAlertsService: SystemAlertsService,
		private progressIndicatorService: ProgressIndicatorService
	) {}

	public getToken(): Observable<string> {
		return new Observable((observer) => {
			if (!this.tokenExpired) {
				observer.next(this.accessToken);
				observer.complete();
			} else {
				if (!this.tokenEmail || !this.refreshToken) {
					this.systemAlertsService.removeAll();
					observer.error();
					observer.complete();
				} else {
					this.tokenRefresh().subscribe(
						() => {
							observer.next(this.accessToken);
							observer.complete();
						},
						() => {
							this.systemAlertsService.removeAll();
							observer.error();
							observer.complete();
						}
					);
				}
			}
		});
	}

	public getDecodedToken(): Observable<IAuthToken> {
		return this.getToken().pipe(map((token: string) => new JwtHelperService().decodeToken<IAuthToken>(token)));
	}

	public extractTokens(email: string, clientId: string, result: any): void {
		const accessToken = result.access_token ?? result.token;
		const refreshToken = result.refresh_token ?? result.refresh;
		const tokenType = result.token_type ?? "id";
		const expiry = result.expires_in ? Date.now() / 1000 + result.expires_in : result.tokenExp;

		if (accessToken && refreshToken && expiry) {
			this.accessToken = accessToken;
			this.refreshToken = refreshToken;
			this.tokenType = tokenType;
			this.tokenExpiry = expiry;
			this.clientId = clientId;

			if (email) {
				this.tokenEmail = email;
			}
		} else {
			this.clearTokens();
		}
	}

	/**
	 * Clear session storage and cookie tokens, this will get rid off all authentication tokens
	 */
	public clearTokens(): void {
		this.storageService.removeStore(StorageType.cookie);
		this.storageService.removeStore(StorageType.sessionStorage);
	}

	public getAccessToken(code: string): Observable<boolean> {
		return new Observable((observer) => {
			const activeSpinner = this.progressIndicatorService.hasSpinner();

			if (!activeSpinner) this.progressIndicatorService.spinner(true);

			this.http
				.post(
					this.endpointService.url(Endpoint.tokenRefresh),
					new HttpParams( { encoder: new CustomURLEncoder() } )
						// HttpParams automatically urlencodes, but incorrectly treats + as space.
						.set('redirect_uri', `${environment.apps.portal}${routeUrl(routePathName.oauthCallback)}`)
						.set('client_id', config.entraClientId)
						.set('grant_type', 'authorization_code')
						.set('code', code)
					,
					{
						headers: new HttpHeaders()
							.set(HttpShareHeaders.z5HttpShare, 'true')
							.set('Api-Key', config.clientId)
							.set('Content-Type', 'application/x-www-form-urlencoded')
					}
				)
				.subscribe(
					(result: any) => {
						this.extractTokens(this.tokenEmail, config.entraClientId, result);
						if (!activeSpinner) this.progressIndicatorService.spinner(false);
						observer.next(true);
						observer.complete();
					},
					(error: any) => {
						this.progressIndicatorService.spinner(false);
						observer.error(error);
						observer.complete();
					}
				);
		});
	}

	private tokenRefresh(): Observable<boolean> {
		return new Observable((observer) => {
			const activeSpinner = this.progressIndicatorService.hasSpinner();

			if (!activeSpinner) this.progressIndicatorService.spinner(true);

			this.http
				.post(
					this.endpointService.url(Endpoint.tokenRefresh),
						new HttpParams( { encoder: new CustomURLEncoder() } )
							// HttpParams automatically urlencodes, but incorrectly treats + as space.
							.set('username', this.tokenEmail)
							.set('client_id', this.clientId)
							.set('grant_type', 'refresh_token')
							.set('refresh_token', this.refreshToken)
					,
					{
						headers: new HttpHeaders()
							.set(HttpShareHeaders.z5HttpShare, 'true')
							.set('Api-Key', config.clientId)
							.set('Content-Type', 'application/x-www-form-urlencoded')
					}
				)
				.subscribe(
					(result: any) => {
						this.extractTokens(this.tokenEmail, this.clientId, result);
						if (!activeSpinner) this.progressIndicatorService.spinner(false);
						observer.next(true);
						observer.complete();
					},
					(error: any) => {
						this.progressIndicatorService.spinner(false);
						observer.error(error);
						observer.complete();
					}
				);
		});
	}
}
